<script setup lang="ts">
import { computed } from 'vue'
import LockedOption from '@/components/LockedOption.vue'
import OpensInNewTabIcon from '@/components/OpensInNewTabIcon.vue'
import { getGoalSubjectLabelFromValue } from '@/contentprofile/no_sel_composable'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { toolkitUrls } from '@/util/links'
import type { GoalIndexSubject } from './goal_index_types'
import SlidePanel from '@/components/SlidePanel.vue'

const ALL_CATEGORIES = 'all'

const props = withDefaults(
  defineProps<{
    subject: GoalIndexSubject
    expanded?: boolean
  }>(),
  { expanded: true }
)

const emit = defineEmits<{
  'update:expanded': [subjectName: string, expanded: boolean]
}>()

const analytics = getAnalyticsTracker()

const subjectLabel = computed(() => getGoalSubjectLabelFromValue(props.subject.subject))
const panelId = computed(() => `lgi-panel-${props.subject.subject.replace(/\W+/g, '-').toLowerCase()}`)

function browseGoalsUrl(category?: string) {
  const filters = new URLSearchParams({ subject: props.subject.subject, ref: 'lgi' })
  if (category) {
    filters.set('subjectDomain', category)
  }
  return `${toolkitUrls.browse}?${filters.toString()}`
}

const categoryEntries = computed(() =>
  (props.subject.categories || []).map((category) => ({
    key: category,
    label: category,
    url: browseGoalsUrl(category),
    category,
    locked: Boolean(props.subject.categories_locked?.includes(category)),
    bulleted: true
  }))
)

const allGoalsEntry = computed(() => ({
  key: ALL_CATEGORIES,
  label: `View all ${subjectLabel.value} goals`,
  url: browseGoalsUrl(),
  category: ALL_CATEGORIES,
  locked: Boolean(props.subject.locked),
  bulleted: false
}))

const entries = computed(() => [...categoryEntries.value, allGoalsEntry.value])

function toggleExpanded() {
  analytics.sendEvent(EVENT_NAMES.GOAL_INDEX.TOGGLED_SUBJECT, {
    lgi_subject: props.subject.subject,
    lgi_expanded: !props.expanded
  })
  emit('update:expanded', props.subject.subject, !props.expanded)
}

function trackOpenedLink(category: string) {
  analytics.sendEvent(EVENT_NAMES.GOAL_INDEX.OPENED_CATEGORY_LINK, {
    lgi_subject: props.subject.subject,
    lgi_category: category
  })
}
</script>

<template>
  <div class="mb-10">
    <h2 class="mb-2.5">
      <button
        class="flex items-center gap-1 text-left text-text-xl font-regular leading-tight text-gray-700"
        :aria-expanded="expanded"
        :aria-controls="panelId"
        @click="toggleExpanded"
      >
        <span
          class="material-icons-round text-2xl transition-transform duration-200 motion-reduce:transition-none"
          :class="{ '-rotate-90': !expanded }"
          aria-hidden="true"
        >
          expand_more
        </span>
        {{ subjectLabel }}
      </button>
    </h2>
    <SlidePanel :expanded="expanded" :notch-inset="9">
      <div>
        <div :id="panelId" class="arrow-box border border-gray-400 bg-gray-50 px-[18px] py-3">
          <ul class="m-0 list-disc pl-5 text-gray-700">
            <li
              v-for="entry in entries"
              :key="entry.key"
              class="group"
              :class="{
                'list-none': entry.locked || !entry.bulleted,
                'cursor-not-allowed': entry.locked,
                'mt-3': !entry.bulleted
              }"
            >
              <LockedOption v-if="entry.locked" :label="entry.label" />
              <a
                v-else
                :href="entry.url"
                target="_blank"
                rel="noopener"
                class="text-blue-400 hover:underline focus:outline-offset-4"
                @click="trackOpenedLink(entry.category)"
              >
                {{ entry.label }}
                <OpensInNewTabIcon
                  class="opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
                />
              </a>
            </li>
          </ul>
        </div>
      </div>
    </SlidePanel>
  </div>
</template>

<style scoped>
.arrow-box {
  position: relative;
  border-radius: 3px;
}

.arrow-box::before,
.arrow-box::after {
  bottom: 100%;
  left: 10%;
  border: solid transparent;
  content: ' ';
  height: 0;
  width: 0;
  position: absolute;
  pointer-events: none;
}

.arrow-box::before {
  border-bottom-color: theme('colors.gray.400');
  border-width: 9px;
  margin-left: -9px;
}

.arrow-box::after {
  border-bottom-color: theme('colors.gray.50');
  border-width: 8px;
  margin-left: -8px;
}
</style>
